import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Server,
  Cpu,
  HardDrive,
  Network,
  Shield,
  Clock,
  ArrowRight,
  Zap,
  Tag,
} from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';

const dedicatedOptions = [
  {
    title: 'Standard Dedicated',
    description: 'Entry-level to mid-range dedicated servers with 1Gbit/s network.',
    icon: Server,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    price: '31.20',
    href: '/dedicated/standard',
    features: ['Intel/AMD CPUs', '32-128GB RAM', 'NVMe SSD', '1Gbit/s'],
  },
  {
    title: 'Premium Dedicated',
    description: 'High-end servers with 10Gbit/s network for maximum performance.',
    icon: Zap,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    price: '189.95',
    href: '/dedicated/premium',
    features: ['High-End CPUs', '128-512GB RAM', 'NVMe RAID', '10Gbit/s'],
    recommended: true,
  },
  {
    title: 'Sale Servers',
    description: 'Discounted dedicated servers with no setup fee. Limited stock!',
    icon: Tag,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    price: '29.99',
    href: 'https://deinserverhost.de/dedicated-sonderposten',
    external: true,
    features: ['Various Configs', 'No Setup Fee', 'Limited Stock', 'Great Deals'],
  },
];

const features = [
  { icon: Shield, text: 'Enterprise DDoS Protection' },
  { icon: Clock, text: '24-48h Setup' },
  { icon: Network, text: 'Up to 10Gbit/s' },
  { icon: Cpu, text: 'Full Root Access' },
];

export default function DedicatedPage() {
  return (
    <Layout>
      <SEOHead
        title="Dedicated Servers – Standard & Premium"
        description={`Dedicated servers without virtualization from €${dedicatedOptions[0].price}/month. Standard (1 Gbit/s) and Premium (10 Gbit/s) – full hardware, DDoS protection, no contract.`}
        canonical="/dedicated"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Dedicated Server Plans',
          url: 'https://deinserverhost.com/dedicated',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Standard Dedicated Server (1 Gbit/s)', url: 'https://deinserverhost.com/dedicated/standard' },
            { '@type': 'ListItem', position: 2, name: 'Premium Dedicated Server (10 Gbit/s)', url: 'https://deinserverhost.com/dedicated/premium' },
            { '@type': 'ListItem', position: 3, name: 'Special Offer Dedicated-Server', url: 'https://deinserverhost.de/dedicated-sonderposten' },
          ],
        }}
      />
      <div className="section-spacing">
        <div className="container-main">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              Dedicated <span className="text-gradient">Servers</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Full physical servers with no virtualization overhead.
              Maximum performance for enterprise workloads.
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-4">
              {features.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 px-4 py-2 rounded-full glass">
                  <Icon className="w-4 h-4 text-primary" />
                  <span className="text-sm">{text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Options */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {dedicatedOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full relative hover:border-primary/50 transition-colors ${option.recommended ? 'border-primary/30 bg-primary/5' : ''}`}>
                  {option.recommended && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">Recommended</Badge>
                  )}
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-xl ${option.bgColor} flex items-center justify-center mb-4`}>
                      <option.icon className={`w-7 h-7 ${option.color}`} />
                    </div>
                    <CardTitle>{option.title}</CardTitle>
                    <CardDescription>{option.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {option.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-sm text-muted-foreground">From</span>
                      <span className="text-3xl font-black text-primary">€{option.price}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>

                    {option.external ? (
                      <a href={option.href} target="_blank" rel="noopener noreferrer">
                        <Button className="w-full bg-primary hover:bg-primary/90">
                          View Servers
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                    ) : (
                      <Link to={option.href}>
                        <Button className="w-full bg-primary hover:bg-primary/90">
                          View Servers
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center p-8 glass rounded-2xl"
          >
            <h2 className="text-2xl font-bold mb-4">Not sure which server to choose?</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Our Product Finder can help you find the perfect solution for your needs.
            </p>
            <Link to="/product-finder">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover">
                Find Your Server
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
